import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, Eye, EyeOff } from 'lucide-react';
import { getBackendUrl } from '../../../utils/api';

const API = `${getBackendUrl()}/api`;

export default function MercadoPagoSettings() {
  const [mpSettings, setMpSettings] = useState({
    mp_access_token: '',
    mp_public_key: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [hasToken, setHasToken] = useState(false);
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/restaurant/mercadopago-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasToken(response.data.has_token);
    } catch (err) {
      console.error('Error loading MP settings:', err);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      if (!mpSettings.mp_access_token) {
        setError('Por favor, insira o Access Token');
        return;
      }

      const token = localStorage.getItem('token');
      await axios.put(`${API}/restaurant/mercadopago-settings`, mpSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Configurações do Mercado Pago salvas com sucesso!');
      setHasToken(true);
      setMpSettings({ mp_access_token: '', mp_public_key: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar configurações');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-500/20 border-2 border-green-500/50 text-green-400 px-5 py-4 rounded-xl text-sm font-medium">
          ✓ {success}
        </div>
      )}
      {error && (
        <div className="bg-red-500/20 border-2 border-red-500/50 text-red-400 px-5 py-4 rounded-xl text-sm font-medium">
          ✗ {error}
        </div>
      )}

      {/* Status Badge */}
      {hasToken && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-400" />
            <div>
              <p className="text-green-400 font-semibold">Mercado Pago Configurado</p>
              <p className="text-gray-400 text-sm">Você já pode receber pagamentos PIX</p>
            </div>
          </div>
        </div>
      )}

      {/* Access Token Input */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-300">
          Access Token *
        </label>
        <div className="relative">
          <input
            type={showToken ? 'text' : 'password'}
            value={mpSettings.mp_access_token}
            onChange={(e) => setMpSettings({ ...mpSettings, mp_access_token: e.target.value })}
            placeholder="APP_USR-..."
            className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors pr-12 font-mono text-sm"
            data-testid="mp-access-token-input"
          />
          <button
            type="button"
            onClick={() => setShowToken(!showToken)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
          >
            {showToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        <p className="text-xs text-gray-500">
          Este token será usado para processar pagamentos PIX na SUA conta
        </p>
      </div>

      {/* Public Key Input (Optional) */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-300">
          Public Key <span className="text-gray-500">(Opcional)</span>
        </label>
        <input
          type="text"
          value={mpSettings.mp_public_key}
          onChange={(e) => setMpSettings({ ...mpSettings, mp_public_key: e.target.value })}
          placeholder="APP_USR-..."
          className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-blue-500 transition-colors font-mono text-sm"
          data-testid="mp-public-key-input"
        />
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={loading}
        className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="mp-save-button"
      >
        {loading ? 'Salvando...' : hasToken ? 'Atualizar Configurações' : 'Salvar Configurações'}
      </button>

      {/* Warning */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <div className="text-yellow-400 text-xl mt-0.5">⚠️</div>
          <div className="flex-1 text-sm text-gray-300">
            <p className="font-semibold text-yellow-400 mb-1">Importante!</p>
            <p>Mantenha suas credenciais seguras. Nunca compartilhe seu Access Token com terceiros.</p>
          </div>
        </div>
      </div>
    </div>
  );
};


// PIX Wait Payment Settings Component
